import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSettings, updatePlanData } from '../Settings/settingSlice';

import { Container, Card, Row, Col, Toast } from 'react-bootstrap';
import Input from "../../components/input";
import { useLocation } from "react-router-dom";
import { Formik } from 'formik';
import CommonFormLabel from "../../components/formLabel";
import { planSchema } from '../../schema/validationSchemas';
import ValidationFeedback from "../../components/validationFeedback";
import { TaskEdit02Icon } from '../../assets/icons';
import CommonButton from '../../components/commonButton';
import { useLoader } from '../../context/LoaderContext';
import SimpleDropdown from "../../components/simpleDropdown";

const planStatusOptions = [
  {value: 'active', label: 'Active'},
  {value: 'expired', label: 'Expired'},
];

const SettingList = () => {
  const dispatch = useDispatch();

  const location = useLocation();
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const selected_organization_id = pathSegments.length > 1 && pathSegments[0] === "organization" 
      ? pathSegments[1] 
      : null;

  const { setLoading } = useLoader();
  const { settings } = useSelector((state) => state.settings);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarVariant, setSnackbarVariant] = useState('success');
  const [editingSection, setEditingSection] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchSettingsData = useCallback(async () => {
    setLoading(true);
    const payload = {
      organization_id: selected_organization_id,
    };
    try {
      await dispatch(getSettings({payload}));
    } catch (error) {
        setLoading(false);
      console.error("Error fetching settings:", error);
    } finally {
        setLoading(false);
    }
  }, [dispatch, setLoading, selected_organization_id]);

  useEffect(() => {
    fetchSettingsData();
  }, [fetchSettingsData]);

  const handleSavePlanData = async (values, { setErrors, setSubmitting }) => {
    try {
      // setLoading(true);
      setIsSubmitting(true);
      setSubmitting(true);
      const res = await dispatch(updatePlanData(values));
      if(res.payload.success === true) {
        setSnackbarMessage(res.payload.message || 'Plan updated successfully');
        setSnackbarVariant('success');
        setEditingSection(null);
        setShowSnackbar(true);
        setIsSubmitting(false);
        setSubmitting(false);
        fetchSettingsData();
      } else {
        setErrors(res.payload.data);
        setSnackbarMessage(res.payload.message || 'Failed to update Plan');
        setSnackbarVariant('danger');
        setShowSnackbar(true);
        setIsSubmitting(false);
        setSubmitting(false);
      }
    } catch (error) {
      setSnackbarMessage('Failed to update Plan');
      setSnackbarVariant('danger');
      setShowSnackbar(true);
      setIsSubmitting(false);
      setSubmitting(false);
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
      // setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
  };

  if (!settings) return <div>Loading...</div>;

  return (
    <>
      <Toast
        onClose={() => setShowSnackbar(false)}
        show={showSnackbar}
        delay={3000}
        autohide
        bg={snackbarVariant}
        style={{ position: 'fixed', top: 20, right: 20, zIndex: 1000 }}
      >
        <Toast.Body style={{ color: 'white' }}>{snackbarMessage}</Toast.Body>
      </Toast>

      <Container fluid className="px-0">
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Plan Details</h5>
            {editingSection !== 'Plan' && (
              <CommonButton
                icon={<TaskEdit02Icon width={16} height={16} color="#ffffff" strokeWidth="2" />}
                title="Edit"
                className="btn-primary primary-shadow"
                onClick={() => setEditingSection('Plan')}
              />
            )}
          </Card.Header>
          <Card.Body>
            {editingSection === 'Plan' ? (
              <Formik
                initialValues={{
                  organization_id: selected_organization_id,
                  plan_start_date: settings?.plan_start_date ? settings.plan_start_date.split(' ')[0] : '',
                  plan_end_date: settings?.plan_end_date ? settings.plan_end_date.split(' ')[0] : '',
                  plan_status: settings?.plan_status || '',
                }}
                validationSchema={planSchema}
                onSubmit={handleSavePlanData}
              >
                {({ values, handleChange, handleSubmit, touched, errors, setFieldValue }) => (
                  <form onSubmit={handleSubmit}>
                    <Row className="gy-4">
                      <Col md={6}>
                        <CommonFormLabel htmlFor="plan_start_date" title="Plan Start Date" />
                        <Input
                          id="plan_start_date"
                          name="plan_start_date"
                          type="date"
                          value={values.plan_start_date}
                          onChange={(e) => {
                            const newStartDate = e.target.value;
                            setFieldValue('plan_start_date', newStartDate);

                            if (values.plan_end_date) {
                              const selectedStart = new Date(newStartDate);
                              const selectedEnd = new Date(values.plan_end_date);

                              // Add 1 day to start date
                              selectedStart.setDate(selectedStart.getDate() + 1);

                              if (selectedEnd < selectedStart) {
                                // If end date is before (start + 1 day), reset it
                                setFieldValue('plan_end_date', '');
                              }
                            }
                          }}
                          className={`shadow-none ${errors?.plan_start_date && touched.plan_start_date ? "error" : "correct"}`}
                        />

                        {touched.plan_start_date && errors?.plan_start_date && (
                          <ValidationFeedback title={errors?.plan_start_date} />
                        )}
                      </Col>

                      <Col md={6} className="mb-4">
                        <CommonFormLabel htmlFor="plan_end_date" title="Plan End Date" />
                        <Input
                          id="plan_end_date"
                          name="plan_end_date"
                          type="date"
                          value={values.plan_end_date}
                          onChange={handleChange}
                          min={
                            values.plan_start_date
                              ? new Date(new Date(values.plan_start_date).setDate(new Date(values.plan_start_date).getDate() + 1))
                                  .toISOString()
                                  .split('T')[0]
                              : new Date().toISOString().split('T')[0]
                          }
                        />
                        {touched.plan_end_date && errors?.plan_end_date && (
                          <ValidationFeedback title={errors?.plan_end_date} />
                        )}
                      </Col>

                      <Col md={6} className="">
                        <SimpleDropdown
                          label="Plan Status"
                          name="plan_status"
                          value={values.plan_status}
                          onChange={(selectedOption) => {
                            setFieldValue("plan_status", selectedOption.value);
                          }}
                          options={planStatusOptions}
                          placeholder="Select a plan status"
                          error={errors?.plan_status}
                          touched={touched.plan_status}
                          className={'mb-4'}
                        />
                      </Col>

                      <Col md={12} className="d-flex justify-content-end gap-3 mt-4">
                        <CommonButton
                          title={isSubmitting ? "Updating..." : "Update"}
                          disabled={isSubmitting}
                          className="btn-primary primary-shadow"
                          type="submit"
                        />
                        <CommonButton
                          title="Cancel"
                          disabled={isSubmitting} 
                          className="btn-muted"
                          type="button"
                          onClick={handleCancelEdit}
                        />
                      </Col>
                    </Row>
                  </form>
                )}
              </Formik>
            ) : (
              <Row className="gy-4">
                <Col md={6}>
                  <CommonFormLabel htmlFor="plan_start_date" title="Plan Start Date" style={{ fontWeight: 'bold', fontSize: '16px' }} />
                  <p>{settings?.plan_start_date ? settings.plan_start_date.split(' ')[0] : '-'}</p>
                </Col>
                <Col md={6}>
                  <CommonFormLabel htmlFor="plan_end_date" title="Plan End Date" style={{ fontWeight: 'bold', fontSize: '16px' }} />
                  <p>{settings?.plan_end_date ? settings.plan_end_date.split(' ')[0] : '-'}</p>
                </Col>
                <Col md={6}>
                  <CommonFormLabel htmlFor="plan_status" title="Plan Status" style={{ fontWeight: 'bold', fontSize: '16px' }} />
                  <p className='text-capitalize'>{settings?.plan_status ? settings?.plan_status : '-'}</p>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default SettingList;
